import { adSlotSizes, adTargetings } from "@config/ads";
import {
  AdTargetingType,
  AdvertisementTypeEnum,
  ArticleAdTargetKeys,
  PageAdTargetValue,
} from "@typings/Ads.d";
import { useDetectAdBlock } from "adblock-detect-react";
import cx from "classnames";
import Cookies from "js-cookie";
import { useCallback, useEffect, useState } from "react";

import styles from "./Advertisement.module.css";
import { generateAdvertElementId, useAdvertLoaded } from "./helpers";

const TYPE = AdvertisementTypeEnum.PRESTITIAL;
const ID = generateAdvertElementId(TYPE);

export type PrestitialAdProps = {
  rootClassName?: string;
  slot: string;
  isPrestitialEnabled: boolean | undefined;
  pubAdTargeting?:
    | AdTargetingType[]
    | AdTargetingType<string, PageAdTargetValue>[];
  topOverlayImpressions: number;
  topOverlayValidity: number;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
};

/**
 * This component renders an Prestitial ad.
 * @param rootClassName - The root class name.
 * @param slot - The ad slot.
 * @see https://developers.google.com/doubleclick-gpt/reference
 * @returns A Prestitial ad component. If the script is not ready, it returns error. If the script is ready, it returns the ad.
 */
export default function PrestitialAd({
  rootClassName,
  slot,
  isPrestitialEnabled,
  pubAdTargeting,
  topOverlayImpressions,
  topOverlayValidity,
  gsChannels,
  articleSlotTarget,
}: PrestitialAdProps) {
  const topoverlayDisplayedCookie = Cookies.get("topoverlayDisplayed");
  const topOverlayImpressionsServedCookie = Cookies.get(
    "topOverlayImpressionsServed",
  );
  let impressionsServed = topOverlayImpressionsServedCookie
    ? parseInt(topOverlayImpressionsServedCookie)
    : 0;

  const isAdLoaded = useAdvertLoaded(TYPE, ID);
  const [error, setError] = useState("");
  const [adSlot, setAdSlot] =
    useState<ReturnType<typeof googletag.defineSlot>>();
  const adSlotSize = adSlotSizes[TYPE];
  const adTarget = adTargetings[TYPE];
  const adBlockDetected = useDetectAdBlock();

  const handleDisplayAd = useCallback(() => {
    try {
      googletag.cmd.push(function () {
        const pageTargeting: Record<string, string | string[]> = {};
        pubAdTargeting?.forEach((target: AdTargetingType) => {
          pageTargeting[target.key] = target.value;
        });

        if (gsChannels) {
          pageTargeting.gs_channels = gsChannels.join(",");
        }

        if (Object.keys(pageTargeting).length > 0) {
          googletag.setConfig({ targeting: pageTargeting });
        }

        googletag
          .pubads()
          .getSlots()
          .forEach(function (_slot) {
            if (_slot.getSlotElementId()) {
              if (_slot.getSlotElementId() === ID) {
                googletag.destroySlots([_slot]);
              }
            }
          });

        const _adSlot = googletag.defineSlot(slot, adSlotSize, ID);

        if (_adSlot) {
          _adSlot.addService(googletag.pubads());

          const slotTargeting: Record<string, string | string[]> = {};
          adTarget?.forEach(({ key, value }) => {
            slotTargeting[key] = value;
          });

          // Article slot targeting.
          articleSlotTarget?.forEach((target) => {
            slotTargeting[target.key] = target.value;
          });

          if (Object.keys(slotTargeting).length > 0) {
            _adSlot.setConfig({ targeting: slotTargeting });
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          window.googletag.slots["prestitial"] = _adSlot;

          googletag.setConfig({ singleRequest: true });
          googletag.enableServices();

          // Store the ad slot for later use.
          setAdSlot(_adSlot);
        }
      });
    } catch (_error: unknown) {
      setError("Please refresh");
    }
  }, [
    slot,
    adSlotSize,
    adTarget,
    pubAdTargeting,
    gsChannels,
    articleSlotTarget,
  ]);

  const closeOverlay = useCallback(() => {
    if (typeof document !== "undefined") {
      document.body.style.overflowY = "visible";
      document.documentElement.style.overflowY = "visible";
      const myPageOverlay = document.getElementById("MyPageOverlay");
      if (myPageOverlay != null) {
        myPageOverlay.style.display = "none";
      }
    }
  }, []);

  const isDFPTest = useCallback(() => {
    const hasDFPTest =
      window.dfp_preview_ids !== undefined &&
      Array.isArray(window.dfp_preview_ids) &&
      window.dfp_preview_ids.length !== 0;

    return Boolean(hasDFPTest);
  }, []);

  useEffect(() => {
    window.adBlockerDetected = adBlockDetected;
    if (
      typeof window !== "undefined" &&
      isPrestitialEnabled === true &&
      !adBlockDetected
    ) {
      if (slot && !adSlot) {
        if (
          ((impressionsServed < topOverlayImpressions &&
            typeof topoverlayDisplayedCookie === "undefined") ||
            isDFPTest()) &&
          screen.width > 767
        ) {
          handleDisplayAd();

          const expiry = new Date();
          expiry.setTime(
            expiry.getTime() + topOverlayValidity * 60 * 60 * 1000,
          );
          const cookieOptions: Cookies.CookieAttributes = {
            path: "/",
            expires: expiry,
          };

          if (!isDFPTest()) {
            impressionsServed++;
            Cookies.set("topoverlayDisplayed", "yes", cookieOptions);
            Cookies.set(
              "topOverlayImpressionsServed",
              impressionsServed === topOverlayImpressions
                ? "0"
                : impressionsServed.toString(),
              cookieOptions,
            );
          }
        } else {
          closeOverlay();
        }
      }
    } else {
      if (isPrestitialEnabled === false || adBlockDetected) {
        closeOverlay();
      } else {
        const timeout = setTimeout(() => {
          closeOverlay();
        }, 3000);

        return () => {
          clearTimeout(timeout);
        };
      }
    }
  }, [
    slot,
    handleDisplayAd,
    adSlot,
    impressionsServed,
    topoverlayDisplayedCookie,
    adBlockDetected,
    isPrestitialEnabled,
    closeOverlay,
    isDFPTest,
    topOverlayImpressions,
    topOverlayValidity,
  ]);

  useEffect(() => {
    return () => {
      const gTag = window.googletag as typeof googletag | undefined;
      if (gTag && gTag.apiReady && adSlot) {
        googletag.cmd.push(function () {
          googletag.destroySlots([adSlot]);
        });
      }
    };
  }, [adSlot]);

  return (
    <>
      <div className={cx(rootClassName, !isAdLoaded && styles.invisible)}>
        {!error ? <div id={ID}></div> : error}
      </div>
    </>
  );
}
